import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import RankName from '../RankName';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folie 34 — Karrierestufen (all 13 ranks).
 * Same ascending-staircase DNA as Worldpools/Rangbonus, extended to 13 cards.
 * Each card: coin badge, rank name, HG requirement, list of unlocked perks
 * (Unilevels, Infinity %, Bonus, Pools). Ascending light→dark palette + taller
 * cards for higher ranks.
 */
export default function SectionKarriere() {
  const { t } = useT();
  const m = t.karriere;

  return (
    <SectionShell id="karriere" className="kr-shell">
      <div className="grid grid-cols-12 gap-y-6 md:gap-12 mb-6 items-end">
        <div className="col-span-12 md:col-span-6">
          <Reveal>
            <div className="overline mb-3">{m.overline}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white max-w-[14ch]">
              {m.title}
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-6 kr-intro">
          <Reveal delay={120}>
            <p className="font-ui text-sm text-white/60 leading-relaxed mb-2">{m.sub}</p>
          </Reveal>
        </div>
      </div>

      {m.forever && (
        <Reveal delay={160}>
          <p className="kr-forever font-display" data-testid="kr-forever">
            {m.forever}
          </p>
        </Reveal>
      )}

      <Reveal delay={200}>
        <div className="kr-matrix-scroll">
          <div className="kr-matrix" data-testid="kr-matrix">
            {m.ranks.map((r, idx) => (
              <div
                key={r.name}
                className="kr-col"
                data-testid={`kr-rank-${idx}`}
                style={{ '--step': idx }}
              >
                {/* Coin sits above the bar */}
                <div className="kr-coin-wrap">
                  <div className="wp-coin">
                    <img
                      src={`/source-pptx/${r.coin}`}
                      alt={`${r.name} Rang`}
                      loading="lazy"
                      draggable={false}
                      className="block w-full h-full object-contain select-none"
                      style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.6))' }}
                    />
                  </div>
                  <div className="kr-rank-name font-display"><RankName name={r.name} /></div>
                </div>

                {/* Ascending bar — HG value rendered VERTICALLY inside.
                    For Ruby (idx===2) we additionally render the first perk
                    ("2 dir. Partner" / locale) inside the bar so the sales-
                    requirement is spatially attached to the rank threshold. */}
                {/* Ascending bar — HG value + optional secondary note both
                    rendered VERTICALLY inside. Ruby (idx===2) gets a second
                    vertical caption ("2 dir. Partner" / locale) placed
                    directly below the HG value along the bar. */}
                <div className={`wp-rank-card kr-rank-card ${idx === 2 ? 'kr-rank-card--dual' : ''}`}>
                  {r.hg && (
                    <div className="kr-hg-vertical font-display" data-testid={`kr-hg-${idx}`}>
                      {r.hg}
                    </div>
                  )}
                  {idx === 2 && r.perks && r.perks[0] && (
                    <div className="kr-hg-vertical kr-hg-vertical--secondary font-ui"
                         data-testid={`kr-bar-note-${idx}`}>
                      {r.perks[0]}
                    </div>
                  )}
                </div>

                {/* Verteilung row — first column shows the "Verteilung:" label
                    so it appears in-line with the % values across the deck. */}
                <div className="kr-dist" data-testid={`kr-dist-${idx}`}>
                  {idx === 0
                    ? <span className="kr-dist-label">{m.distributionLabel || '**Verteilung:'}</span>
                    : (r.dist || '\u00A0')}
                </div>

                {/* Perks list — Ruby's first perk moved inside the bar,
                    so skip it here to avoid duplication. */}
                <ul className="kr-perks">
                  {(idx === 2 ? r.perks.slice(1) : r.perks).map((p, i) => (
                    <li key={i} className="kr-perk font-ui">{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={620}>
        <p className="font-ui text-xs text-white/40 italic text-center mt-8">
          {m.footnote}
        </p>
      </Reveal>
    </SectionShell>
  );
}
